import { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';

const PrizeGallery = ({ competition }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Early return if no valid images
  if (!competition?.images || competition?.images?.length === 0) {
    return (
      <div className="bg-card rounded-[6px] border border-border overflow-hidden">
        <div className="relative aspect-square lg:aspect-[4/3] bg-muted overflow-hidden flex items-center justify-center">
          <div className="text-center text-muted-foreground p-8">
            <Icon name="PhotoIcon" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No images available</p>
          </div>
        </div>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentImageIndex((prev) => 
      prev === competition?.images?.length - 1 ? 0 : prev + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => 
      prev === 0 ? competition?.images?.length - 1 : prev - 1
    );
  };

  const handleThumbnailClick = (index, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setCurrentImageIndex(index);
  };

  // Get current image with fallbacks
  const currentImage = competition?.images?.[currentImageIndex];
  const imageSrc = currentImage?.url || '/assets/images/no_image.png';
  const imageAlt = currentImage?.alt || 'Competition prize image';

  return (
    <div className="bg-card rounded-[6px] border border-border overflow-hidden">
      {/* Main image. Prize photos are square: phones show them edge to edge;
          wider screens show the whole square over a blurred copy of itself,
          so the photo is never cropped or a screen tall. */}
      <div className="relative aspect-square lg:aspect-[4/3] bg-muted overflow-hidden">
        <AppImage
          src={imageSrc}
          alt=""
          aria-hidden="true"
          className="hidden lg:block absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40"
        />
        <AppImage
          src={imageSrc}
          alt={imageAlt}
          className="relative w-full h-full object-cover lg:object-contain outline outline-1 -outline-offset-1 outline-white/10"
        />
        
        {/* Navigation Arrows */}
        {competition?.images?.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-smooth active:scale-[0.96] z-10"
            >
              <Icon name="ChevronLeftIcon" size={20} />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center transition-smooth active:scale-[0.96] z-10"
            >
              <Icon name="ChevronRightIcon" size={20} />
            </button>
          </>
        )}

        {/* Image Counter */}
        {competition?.images?.length > 1 && (
          <div className="absolute bottom-4 right-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm z-10">
            {currentImageIndex + 1} / {competition?.images?.length}
          </div>
        )}
      </div>
      
      {/* Thumbnail Strip */}
      {competition?.images?.length > 1 && (
        <div className="p-4 border-t border-border">
          <div className="flex space-x-2 overflow-x-auto">
            {competition?.images?.map((image, index) => (
              <button
                key={index}
                type="button"
                onClick={(e) => handleThumbnailClick(index, e)}
                className={`flex-shrink-0 w-16 h-16 rounded-[6px] overflow-hidden border-2 transition duration-200 cursor-pointer relative ${
                  index === currentImageIndex
                    ? 'border-accent ring-2 ring-accent/20' :'border-transparent hover:border-muted-foreground'
                }`}
                aria-label={`View image ${index + 1}`}
              >
                <AppImage
                  src={image?.url || '/assets/images/no_image.png'}
                  alt={image?.alt || `Competition image ${index + 1}`}
                  className="w-full h-full object-cover pointer-events-none"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

PrizeGallery.propTypes = {
  competition: PropTypes?.shape({
    images: PropTypes?.arrayOf(
      PropTypes?.shape({
        url: PropTypes?.string?.isRequired,
        alt: PropTypes?.string?.isRequired
      })
    )?.isRequired
  })?.isRequired
};

export default PrizeGallery;